import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React, { useEffect, useRef, useState } from 'react'
import { Collapse, Overlay, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { environment, SETTINGS_KEYS } from '../../config/environment'
import { MODULE_DEFAULT_ENABLED, ModuleKey } from '../../constants/module-keys'
import { useSetting } from '../../hooks/useSettings'
import { type GroupedByDocWfRow, documentWorkflowTaskService } from '../../services/document-workflow-task.service'
import { openDocumentsService } from '../../services/open-documents.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { savedViewService, sortSavedViewsByStoredOrder } from '../../services/saved-view.service'
import { settingsService } from '../../services/settings.service'
import { toastService } from '../../services/toast.service'
import { useAppStore } from '../../store/useAppStore'
import { ApiResponse, Document, SavedView } from '../../types'
import { useModuleFlag } from '../../utils/featureFlags'
import { ConfirmDialog } from '../common/ConfirmDialog'
import IfPermissions from '../common/IfPermissions'

type PermissionGate = { action: PermissionAction; type: PermissionType }

type SidebarNavItem = {
  id: string
  label: string
  icon: string
  to?: string
  /**
   * Single permission gate, alternatives (anyOf), or required combined gates (allOf).
   * `anyOf` is used for multi-tab pages; `allOf` is used when a visible link
   * would otherwise open a page without enough API access.
   */
  permission?: PermissionGate | { anyOf: PermissionGate[] } | { allOf: PermissionGate[] }
  disabled?: boolean
  disabledTooltip?: string
  children?: SidebarNavItem[]
  /** When true, link is rendered with sidebar-link--sub (e.g. under a collapsible group) */
  sub?: boolean
}

type SlimPanelType = 'savedViews' | 'openDocuments' | null

interface SortableSidebarSavedViewProps {
  view: SavedView
  isActive: boolean
  showCount: boolean
  dragLabel: string
  onNavigate: () => void
  renderLinkWithTooltip: (link: React.ReactElement) => React.ReactElement
}

const SortableSidebarSavedView: React.FC<SortableSidebarSavedViewProps> = ({
  view,
  isActive,
  showCount,
  dragLabel,
  onNavigate,
  renderLinkWithTooltip,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: view.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const linkElement = (
    <Link className={`sidebar-link sidebar-link--sub ${isActive ? 'active' : ''}`} to={`/view/${view.id}`} onClick={onNavigate}>
      <span className="sidebar-link__icon" aria-hidden="true">
        <i className="bi bi-funnel"></i>
      </span>
      <span className="sidebar-link__label text-truncate">{view.name}</span>
      {showCount && view.document_count !== undefined && (
        <span className="sidebar-link__badge sidebar-link__badge--sub">{view.document_count}</span>
      )}
    </Link>
  )

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`sidebar-link__item sidebar-sublist__item sidebar-sortable-item ${isDragging ? 'is-dragging' : ''}`}
    >
      <div className="sidebar-sortable-item__row">
        <button
          type="button"
          className="sidebar-link__drag-handle"
          aria-label={dragLabel}
          title={dragLabel}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          {...attributes}
          {...listeners}
        >
          <i className="bi bi-grip-vertical"></i>
        </button>
        {renderLinkWithTooltip(linkElement)}
      </div>
    </li>
  )
}

export const Sidebar: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { isMenuCollapsed, setIsMenuCollapsed, savedViews, setSavedViews } = useAppStore()
  const [openDocuments, setOpenDocuments] = useState<Document[]>([])
  const [showCloseDocumentDialog, setShowCloseDocumentDialog] = useState(false)
  const [showCloseAllDocumentsDialog, setShowCloseAllDocumentsDialog] = useState(false)
  const [documentToClose, setDocumentToClose] = useState<Document | null>(null)
  const isEmailIntegrationEnabled = useModuleFlag(ModuleKey.EmailIntegration, MODULE_DEFAULT_ENABLED[ModuleKey.EmailIntegration])
  const isSavedViewsEnabled = useModuleFlag(ModuleKey.SavedViews, MODULE_DEFAULT_ENABLED[ModuleKey.SavedViews])
  const isWorkflowEnabled = useModuleFlag(ModuleKey.Workflow, MODULE_DEFAULT_ENABLED[ModuleKey.Workflow])
  const canViewSavedViewsData = permissionsService.currentUserCan(PermissionAction.View, PermissionType.SavedView)
  const canViewWorkflowTasks = permissionsService.currentUserCan(
    PermissionAction.Display,
    PermissionType.DocumentWorkflowTask,
  )
  const canViewDocumentConfig =
    permissionsService.currentUserCan(PermissionAction.Display, PermissionType.DocumentType) ||
    permissionsService.currentUserCan(PermissionAction.Display, PermissionType.WorkflowStatus) ||
    permissionsService.currentUserCan(PermissionAction.Display, PermissionType.DocumentWorkflowDefinition) ||
    permissionsService.currentUserCan(PermissionAction.Display, PermissionType.CustomField)

  // Učitaj stanje iz localStorage pri inicijalizaciji
  const getInitialCollapsedState = (key: string, defaultValue: boolean): boolean => {
    try {
      const stored = localStorage.getItem(key)
      return stored !== null ? JSON.parse(stored) : defaultValue
    } catch {
      return defaultValue
    }
  }

  const [savedViewsCollapsed, setSavedViewsCollapsed] = useState(() => getInitialCollapsedState('sidebar-saved-views-collapsed', true))
  const [openDocumentsCollapsed, setOpenDocumentsCollapsed] = useState(() => getInitialCollapsedState('sidebar-open-documents-collapsed', true))
  const [documentConfigCollapsed, setDocumentConfigCollapsed] = useState(() => getInitialCollapsedState('sidebar-document-config-collapsed', true))
  const [slimPanelType, setSlimPanelType] = useState<SlimPanelType>(null)
  const [slimPanelTarget, setSlimPanelTarget] = useState<HTMLElement | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [activeWorkflowTasksCount, setActiveWorkflowTasksCount] = useState<number>(0)
  const sidebarSurfaceRef = useRef<HTMLDivElement | null>(null)

  // Fetch open (document, workflow) group count — same grouping/filter as Moji zadaci list
  useEffect(() => {
    if (!isWorkflowEnabled || !canViewWorkflowTasks) {
      setActiveWorkflowTasksCount(0)
      return
    }
    let cancelled = false
    documentWorkflowTaskService
      .getGroupedTasksByDocWf({ status: 'open', page: 1, page_size: 1 })
      .then((res: ApiResponse<GroupedByDocWfRow>) => {
        if (!cancelled) setActiveWorkflowTasksCount(res.count ?? 0)
      })
      .catch(() => {
        if (!cancelled) setActiveWorkflowTasksCount(0)
      })
    return () => {
      cancelled = true
    }
  }, [isWorkflowEnabled, canViewWorkflowTasks])

  const slimSidebar = useSetting<boolean>(SETTINGS_KEYS.SLIM_SIDEBAR, false)
  const sidebarViewsShowCount = useSetting<boolean>(SETTINGS_KEYS.SIDEBAR_VIEWS_SHOW_COUNT, true)
  const appLogoPath = useSetting<string | null>(SETTINGS_KEYS.APP_LOGO, null)
  const savedViewsListId = 'sidebar-saved-views-list'
  const openDocumentsListId = 'sidebar-open-documents-list'
  const documentConfigListId = 'sidebar-document-config-list'

  const resolveAssetUrl = (path?: string | null) => {
    if (!path) return null
    if (path.startsWith('http://') || path.startsWith('https://')) return path
    return environment.apiBaseUrl.replace(/\/api\/$/, path)
  }

  const sidebarLogoSrc = resolveAssetUrl(appLogoPath) || environment.appLogo

  // Provera da li je mobilni uređaj (ne prikazujemo slim mod na mobilnom)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Sačuvaj stanje u localStorage kada se promeni
  useEffect(() => {
    try {
      localStorage.setItem('sidebar-saved-views-collapsed', JSON.stringify(savedViewsCollapsed))
    } catch (error) {
      // Ignoriši greške pri čuvanju u localStorage
    }
  }, [savedViewsCollapsed])

  useEffect(() => {
    try {
      localStorage.setItem('sidebar-open-documents-collapsed', JSON.stringify(openDocumentsCollapsed))
    } catch (error) {
      // Ignoriši greške pri čuvanju u localStorage
    }
  }, [openDocumentsCollapsed])

  useEffect(() => {
    try {
      localStorage.setItem('sidebar-document-config-collapsed', JSON.stringify(documentConfigCollapsed))
    } catch (error) {
      // Ignoriši greške pri čuvanju u localStorage
    }
  }, [documentConfigCollapsed])

  useEffect(() => {
    if (isSavedViewsEnabled && canViewSavedViewsData) {
      loadSavedViews()
    } else {
      setSavedViews([])
    }

    // Subscribe to open documents changes
    const updateOpenDocs = () => {
      setOpenDocuments([...openDocumentsService.getOpenDocuments()])
    }
    updateOpenDocs() // Initial load
    const unsubscribe = openDocumentsService.subscribe(updateOpenDocs)
    return unsubscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSavedViewsEnabled, canViewSavedViewsData])

  useEffect(() => {
    if (!slimSidebar || isMobile) {
      closeSlimPanel()
    }
  }, [slimSidebar, isMobile])

  useEffect(() => {
    if (!slimSidebar || isMobile || !slimPanelType) return
    const handleClickOutside = (event: MouseEvent) => {
      const panel = document.querySelector('.sidebar-slim-panel')
      if (panel && panel.contains(event.target as Node)) {
        return
      }
      if ((event.target as HTMLElement | null)?.closest('.sidebar-slim-trigger')) {
        return
      }
      closeSlimPanel()
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSlimPanel()
      }
    }
    window.addEventListener('click', handleClickOutside)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('click', handleClickOutside)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [slimSidebar, isMobile, slimPanelType])

  const loadSavedViews = async () => {
    try {
      const response = await savedViewService.getSavedViews({ page_size: 100 })
      const sidebarViews = response.results.filter((view) => view.show_in_sidebar)
      const viewsWithCounts = await savedViewService.loadDocumentCounts(sidebarViews)
      const storedOrder: number[] = settingsService.get(SETTINGS_KEYS.SIDEBAR_VIEWS_SORT_ORDER) || []
      setSavedViews(sortSavedViewsByStoredOrder(viewsWithCounts, storedOrder))
    } catch (error) {}
  }

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleSavedViewsDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }
    const oldIndex = savedViews.findIndex((v) => v.id === active.id)
    const newIndex = savedViews.findIndex((v) => v.id === over.id)
    if (oldIndex < 0 || newIndex < 0) {
      return
    }
    const reordered = arrayMove(savedViews, oldIndex, newIndex)
    setSavedViews(reordered)
    settingsService.updateSidebarViewsSort(reordered.map((v) => v.id)).catch(() => {
      toastService.showError(t('error_saving_settings') || 'Error saving settings')
    })
  }

  const toggleSlimSidebar = async () => {
    settingsService.set(SETTINGS_KEYS.SLIM_SIDEBAR, !slimSidebar)

    try {
      await settingsService.storeSettings()
    } catch (error) {}
  }

  const closeMenu = () => {
    setIsMenuCollapsed(true)
  }

  const closeDocument = (doc: Document, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (openDocumentsService.isDirty(doc)) {
      setDocumentToClose(doc)
      setShowCloseDocumentDialog(true)
    } else {
      openDocumentsService.closeDocument(doc)

      if (location.pathname === `/documents/${doc.id}` || location.pathname === `/documents/${doc.id}/details`) {
        navigate('/documents')
      }
    }
  }

  const handleConfirmCloseDocument = () => {
    if (documentToClose) {
      openDocumentsService.closeDocument(documentToClose)

      if (location.pathname === `/documents/${documentToClose.id}` || location.pathname === `/documents/${documentToClose.id}/details`) {
        navigate('/documents')
      }

      setDocumentToClose(null)
      setShowCloseDocumentDialog(false)
    }
  }

  const handleCancelCloseDocument = () => {
    setDocumentToClose(null)
    setShowCloseDocumentDialog(false)
  }

  const closeAllDocuments = (event: React.MouseEvent) => {
    event.preventDefault()

    if (openDocumentsService.hasDirty()) {
      setShowCloseAllDocumentsDialog(true)
    } else {
      openDocumentsService.closeAll()

      if (location.pathname.startsWith('/documents/')) {
        navigate('/documents')
      }
    }
  }

  const handleConfirmCloseAllDocuments = () => {
    openDocumentsService.closeAll()

    if (location.pathname.startsWith('/documents/')) {
      navigate('/documents')
    }

    setShowCloseAllDocumentsDialog(false)
  }

  const handleCancelCloseAllDocuments = () => {
    setShowCloseAllDocumentsDialog(false)
  }

  const isActive = (path: string) => {
    if (path.includes('?')) {
      const [p, q] = path.split('?')
      if (location.pathname !== p) return false
      const want = new URLSearchParams(q)
      const have = new URLSearchParams(location.search)
      for (const [k, v] of want) if (have.get(k) !== v) return false
      return true
    }
    const onTasks = location.pathname === '/documents' && new URLSearchParams(location.search).get('assigned_to_me') === 'true'
    if (path === '/documents' && onTasks) return false
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const toggleSavedViewsSection = () => {
    setSavedViewsCollapsed((prev) => !prev)
  }

  const toggleOpenDocumentsSection = () => {
    setOpenDocumentsCollapsed((prev) => !prev)
  }

  const toggleDocumentConfigSection = () => {
    setDocumentConfigCollapsed((prev) => !prev)
  }

  const closeSlimPanel = () => {
    setSlimPanelType(null)
    setSlimPanelTarget(null)
  }

  const handleSlimPanelToggle = (panel: Exclude<SlimPanelType, null>, event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (slimPanelType === panel) {
      closeSlimPanel()
    } else {
      setSlimPanelType(panel)
      setSlimPanelTarget(event.currentTarget)
    }
  }

  const primaryLinks: SidebarNavItem[] = [
    {
      id: 'dashboard',
      label: t('dashboard'),
      icon: 'house',
      to: '/dashboard',
    },
    {
      id: 'documents',
      label: t('documents'),
      icon: 'files',
      to: '/documents',
      permission: {
        action: PermissionAction.Display,
        type: PermissionType.Document,
      },
    },
    {
      id: 'documentworkflowtasks',
      label: t('document_workflow.sidebar_my_tasks'),
      icon: 'bi bi-list-check',
      to: '/documentworkflowtasks',
      permission: {
        action: PermissionAction.Display,
        type: PermissionType.DocumentWorkflowTask,
      },
    },
  ]

  const primaryLinksWithModules = primaryLinks.map((item) => {
    if (item.id === 'documentworkflowtasks') {
      return {
        ...item,
        disabled: !isWorkflowEnabled,
        disabledTooltip: t('workflow_disabled_message'),
      }
    }
    return item
  })

  const manageLinks: SidebarNavItem[] = [
    {
      id: 'tags',
      label: t('tags'),
      icon: 'tags',
      to: '/tags',
      permission: {
        action: PermissionAction.Display,
        type: PermissionType.Tag,
      },
    },
    {
      id: 'correspondents',
      label: t('correspondents'),
      icon: 'person',
      to: '/correspondents',
      permission: {
        action: PermissionAction.Display,
        type: PermissionType.Correspondent,
      },
    },
    {
      id: 'storagepaths',
      label: t('storage_paths'),
      icon: 'folder',
      to: '/storagepaths',
      permission: {
        action: PermissionAction.Display,
        type: PermissionType.StoragePath,
      },
    },
    {
      id: 'document_config',
      label: t('document_configuration'),
      icon: 'diagram-3',
      children: [
        {
          id: 'customfields',
          label: t('custom_fields'),
          icon: 'ui-radios',
          to: '/customfields',
          permission: {
            action: PermissionAction.Display,
            type: PermissionType.CustomField,
          },
        },
        {
          id: 'documenttypes',
          label: t('types'),
          icon: 'hash',
          to: '/documenttypes',
          permission: {
            action: PermissionAction.Display,
            type: PermissionType.DocumentType,
          },
        },
        {
          id: 'workflowstatuses',
          label: t('document_workflow.sidebar_statuses'),
          icon: 'flag',
          to: '/workflowstatuses',
          permission: {
            action: PermissionAction.Display,
            type: PermissionType.WorkflowStatus,
          },
        },
        {
          id: 'documentworkflowdefinitions',
          label: t('document_workflow.sidebar_workflow'),
          icon: 'diagram-3',
          to: '/documentworkflowdefinitions',
          permission: {
            action: PermissionAction.Display,
            type: PermissionType.DocumentWorkflowDefinition,
          },
        },
      ],
    },
    {
      id: 'savedviews',
      label: t('saved_views'),
      icon: 'window-stack',
      to: '/savedviews',
      permission: {
        allOf: [
          { action: PermissionAction.Display, type: PermissionType.SavedView },
          { action: PermissionAction.View, type: PermissionType.SavedView },
        ],
      },
    },
    {
      id: 'email',
      label: t('email'),
      icon: 'envelope',
      to: '/email',
      // Email page has multiple tabs; show the link if user can see any of them.
      permission: {
        anyOf: [
          { action: PermissionAction.Display, type: PermissionType.MailAccount },
          { action: PermissionAction.Display, type: PermissionType.MailRule },
        ],
      },
    },
    {
      id: 'workflows',
      label: t('workflows'),
      icon: 'boxes',
      to: '/workflows',
      permission: {
        action: PermissionAction.Display,
        type: PermissionType.Workflow,
      },
    },
  ]

  const manageLinksWithModules = manageLinks.map((item) => {
    if (item.id === 'email') {
      return {
        ...item,
        disabled: !isEmailIntegrationEnabled,
        disabledTooltip: t('email_integration_disabled_message'),
      }
    }
    if (item.id === 'savedviews') {
      return {
        ...item,
        disabled: !isSavedViewsEnabled,
        disabledTooltip: t('saved_views_disabled_message'),
      }
    }
    if (item.id === 'workflows') {
      return {
        ...item,
        disabled: !isWorkflowEnabled,
        disabledTooltip: t('workflow_disabled_message'),
      }
    }
    if (item.id === 'document_config') {
      return {
        ...item,
        disabled: !isWorkflowEnabled,
        disabledTooltip: t('workflow_disabled_message'),
      }
    }
    return item
  })

  const adminLinks: SidebarNavItem[] = [
    {
      id: 'settings',
      label: t('settings'),
      icon: 'gear',
      to: '/settings',
      permission: {
        action: PermissionAction.Display,
        type: PermissionType.UISettings,
      },
    },
    {
      id: 'usersgroups',
      label: t('users_and_groups'),
      icon: 'people',
      to: '/usersgroups',
      // Page has 3 tabs (Organizations / Users / Groups); show link if any is visible.
      permission: {
        anyOf: [
          { action: PermissionAction.Display, type: PermissionType.Organization },
          { action: PermissionAction.Display, type: PermissionType.User },
          { action: PermissionAction.Display, type: PermissionType.Group },
        ],
      },
    },
    {
      id: 'tasks',
      label: t('document_processing'),
      icon: 'gear-wide-connected',
      to: '/tasks',
      permission: {
        action: PermissionAction.Display,
        type: PermissionType.DocDotTask,
      },
    },
    {
      id: 'trash',
      label: t('trash'),
      icon: 'trash',
      to: '/trash',
      permission: {
        action: PermissionAction.Display,
        type: PermissionType.Document,
      },
    },
  ]

  const renderNavItem = (item: SidebarNavItem) => {
    if (!item) {
      return null
    }
    const isDisabled = Boolean(item.disabled)
    const tooltipText = isDisabled ? item.disabledTooltip || item.label : item.label
    const linkContent = (
      <>
        <span className="sidebar-link__icon" aria-hidden="true">
          <i className={`bi bi-${item.icon}`}></i>
        </span>
        <span className="sidebar-link__label">{item.label}</span>
        {item.id === 'documentworkflowtasks' && activeWorkflowTasksCount > 0 && (
          <span className="sidebar-link__badge" aria-label={t('document_workflow.active_tasks_count', { count: activeWorkflowTasksCount })}>
            {activeWorkflowTasksCount > 99 ? '99+' : activeWorkflowTasksCount}
          </span>
        )}
      </>
    )

    const linkClass = ['sidebar-link', isActive(item.to ?? '') ? 'active' : '', item.sub ? 'sidebar-link--sub' : ''].filter(Boolean).join(' ')
    const linkElement = isDisabled ? (
      <button type="button" className="sidebar-link sidebar-link--disabled" disabled title={item.disabledTooltip || item.label} aria-disabled="true">
        {linkContent}
      </button>
    ) : (
      <Link className={linkClass} to={item.to ?? '#'} onClick={closeMenu}>
        {linkContent}
      </Link>
    )

    const listItem = (
      <li className={`sidebar-link__item ${isDisabled ? 'sidebar-link__item--disabled' : ''}`}>
        <TooltipWrapper text={tooltipText} id={item.id}>
          {linkElement}
        </TooltipWrapper>
      </li>
    )

    if (item.permission) {
      if ('anyOf' in item.permission) {
        const visible = item.permission.anyOf.some((gate) => permissionsService.currentUserCan(gate.action, gate.type))
        if (!visible) {
          return null
        }
        return React.cloneElement(listItem, { key: item.id })
      }
      if ('allOf' in item.permission) {
        const visible = item.permission.allOf.every((gate) => permissionsService.currentUserCan(gate.action, gate.type))
        if (!visible) {
          return null
        }
        return React.cloneElement(listItem, { key: item.id })
      }
      return (
        <IfPermissions key={item.id} action={item.permission.action} type={item.permission.type}>
          {listItem}
        </IfPermissions>
      )
    }

    return React.cloneElement(listItem, { key: item.id })
  }

  const renderManageItem = (item: SidebarNavItem) => {
    if (item.children) {
      return (
        <li
          key={item.id}
          className={`sidebar-link__item sidebar-link__item--group ${item.id === 'document_config' ? 'sidebar-link__item--document-config' : ''}`}
        >
          <TooltipWrapper text={item.disabled ? item.disabledTooltip || item.label : item.label} id={item.id}>
            <button
              type="button"
              className={`sidebar-section__heading sidebar-section__heading--toggle sidebar-link ${item.disabled ? 'sidebar-link--disabled' : ''}`}
              aria-expanded={!documentConfigCollapsed}
              aria-controls={documentConfigListId}
              onClick={toggleDocumentConfigSection}
              disabled={item.disabled}
              title={item.disabled ? item.disabledTooltip : undefined}
            >
              <span className="sidebar-section__heading-text">
                <i className={`bi bi-chevron-${documentConfigCollapsed ? 'right' : 'down'}`}></i>
                <span className="sidebar-link__icon" aria-hidden="true">
                  <i className={`bi bi-${item.icon}`}></i>
                </span>
                <span className="sidebar-link__label">{item.label}</span>
              </span>
            </button>
          </TooltipWrapper>
          <Collapse in={!documentConfigCollapsed}>
            <div id={documentConfigListId}>
              <ul className="sidebar-section__list sidebar-sublist__list">
                {item.children.map((child) =>
                  renderNavItem({
                    ...child,
                    sub: true,
                    disabled: item.disabled || child.disabled,
                    disabledTooltip: item.disabled ? item.disabledTooltip : child.disabledTooltip,
                  })
                )}
              </ul>
            </div>
          </Collapse>
        </li>
      )
    }
    return renderNavItem(item)
  }

  // Tooltip wrapper komponenta za slim mod
  const TooltipWrapper: React.FC<{ children: React.ReactElement; text: string; id: string }> = ({ children, text, id }) => {
    if (slimSidebar) {
      return (
        <OverlayTrigger
          placement="right"
          delay={{ show: 300, hide: 100 }}
          overlay={
            <Tooltip id={`tooltip-${id}`} className="slim-tooltip">
              {text}
            </Tooltip>
          }
        >
          {children}
        </OverlayTrigger>
      )
    }
    return children
  }

  return (
    <>
      <Collapse in={!isMenuCollapsed} timeout={0}>
        <aside
          id="sidebarMenu"
          className={`collapse d-md-flex app-shell__sidebar ${slimSidebar ? 'app-shell__sidebar--slim' : ''}`}
          aria-label={t('sidebar')}
        >
          <div className="sidebar-surface" ref={sidebarSurfaceRef}>
            <button
              className="sidebar-slim-toggler"
              type="button"
              aria-pressed={slimSidebar}
              aria-label={t('slim_sidebar')}
              title={t('slim_sidebar')}
              onClick={toggleSlimSidebar}
            >
              <i className={`bi bi-chevron-double-${slimSidebar ? 'right' : 'left'}`}></i>
            </button>

            {isMobile && (
              <button className="sidebar-mobile-close" type="button" onClick={closeMenu} aria-label={t('close')} title={t('close')}>
                <i className="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            )}

            <nav className="sidebar__nav" aria-label={t('sidebar')}>
              <div className="sidebar__scroll">
                <section className="sidebar-section">
                  <ul className="sidebar-section__list">{primaryLinksWithModules.map((item) => renderNavItem(item))}</ul>
                </section>

                {isSavedViewsEnabled && canViewSavedViewsData && savedViews.length > 0 && (
                  <section className="sidebar-section">
                    {slimSidebar && !isMobile ? (
                      <TooltipWrapper text={t('saved_views')} id="saved-views-slim-trigger">
                        <button
                          type="button"
                          className={`sidebar-slim-trigger ${slimPanelType === 'savedViews' ? 'active' : ''}`}
                          aria-expanded={slimPanelType === 'savedViews'}
                          onClick={(event) => handleSlimPanelToggle('savedViews', event)}
                        >
                          <i className="bi bi-window-stack" aria-hidden="true"></i>
                          <span className="sidebar-slim-trigger__badge">{savedViews.length}</span>
                        </button>
                      </TooltipWrapper>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="sidebar-section__heading sidebar-section__heading--toggle"
                          aria-expanded={!savedViewsCollapsed}
                          aria-controls={savedViewsListId}
                          onClick={toggleSavedViewsSection}
                        >
                          <span className="sidebar-section__heading-text">
                            <i className={`bi bi-chevron-${savedViewsCollapsed ? 'right' : 'down'}`}></i>
                            {t('saved_views')}
                          </span>
                          <span className="sidebar-section__counter">{savedViews.length}</span>
                        </button>
                        <Collapse in={!savedViewsCollapsed}>
                          <div id={savedViewsListId}>
                            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleSavedViewsDragEnd}>
                              <SortableContext items={savedViews.map((v) => v.id)} strategy={verticalListSortingStrategy}>
                                <ul className="sidebar-section__list sidebar-sublist__list">
                                  {savedViews.map((view) => (
                                    <SortableSidebarSavedView
                                      key={view.id}
                                      view={view}
                                      isActive={isActive(`/view/${view.id}`)}
                                      showCount={Boolean(sidebarViewsShowCount)}
                                      dragLabel={t('drag_to_reorder')}
                                      onNavigate={closeMenu}
                                      renderLinkWithTooltip={(linkElement) => (
                                        <TooltipWrapper text={view.name} id={`view-${view.id}`}>
                                          {linkElement}
                                        </TooltipWrapper>
                                      )}
                                    />
                                  ))}
                                </ul>
                              </SortableContext>
                            </DndContext>
                          </div>
                        </Collapse>
                      </>
                    )}
                  </section>
                )}

                <IfPermissions action={PermissionAction.Display} type={PermissionType.Document}>
                  {openDocuments.length > 0 && (
                    <section className="sidebar-section">
                      {slimSidebar && !isMobile ? (
                        <TooltipWrapper text={t('open_documents')} id="open-documents-slim-trigger">
                          <button
                            type="button"
                            className={`sidebar-slim-trigger ${slimPanelType === 'openDocuments' ? 'active' : ''}`}
                            aria-expanded={slimPanelType === 'openDocuments'}
                            onClick={(event) => handleSlimPanelToggle('openDocuments', event)}
                          >
                            <i className="bi bi-file-text" aria-hidden="true"></i>
                            <span className="sidebar-slim-trigger__badge">{openDocuments.length}</span>
                          </button>
                        </TooltipWrapper>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="sidebar-section__heading sidebar-section__heading--toggle"
                            aria-expanded={!openDocumentsCollapsed}
                            aria-controls={openDocumentsListId}
                            onClick={toggleOpenDocumentsSection}
                          >
                            <span className="sidebar-section__heading-text">
                              <i className={`bi bi-chevron-${openDocumentsCollapsed ? 'right' : 'down'}`}></i>
                              {t('open_documents')}
                            </span>
                            <span className="sidebar-section__counter">{openDocuments.length}</span>
                          </button>
                          <Collapse in={!openDocumentsCollapsed}>
                            <div id={openDocumentsListId}>
                              <ul className="sidebar-section__list sidebar-sublist__list">
                                {openDocuments.map((doc) => (
                                  <li key={doc.id} className="sidebar-link__item sidebar-sublist__item">
                                    <TooltipWrapper text={doc.title} id={`doc-${doc.id}`}>
                                      <Link
                                        className={`sidebar-link sidebar-link--document sidebar-link--sub ${isActive(`/documents/${doc.id}`) ? 'active' : ''}`}
                                        to={`/documents/${doc.id}/details`}
                                        onClick={closeMenu}
                                      >
                                        <span className="sidebar-link__icon" aria-hidden="true">
                                          <i className="bi bi-file-text"></i>
                                        </span>
                                        <span className="sidebar-link__label text-truncate">{doc.title}</span>
                                        <button
                                          type="button"
                                          className="sidebar-link__close"
                                          onClick={(event) => closeDocument(doc, event)}
                                          aria-label={t('close')}
                                        >
                                          <i className="bi bi-x"></i>
                                        </button>
                                      </Link>
                                    </TooltipWrapper>
                                  </li>
                                ))}
                                {openDocuments.length >= 1 && (
                                  <li className="sidebar-link__item sidebar-sublist__item">
                                    <TooltipWrapper text={t('close_all')} id="close-all-docs">
                                      <button
                                        type="button"
                                        className="sidebar-link sidebar-link--ghost sidebar-link--sub"
                                        onClick={closeAllDocuments}
                                      >
                                        <span className="sidebar-link__icon" aria-hidden="true">
                                          <i className="bi bi-x"></i>
                                        </span>
                                        <span>{t('close_all')}</span>
                                      </button>
                                    </TooltipWrapper>
                                  </li>
                                )}
                              </ul>
                            </div>
                          </Collapse>
                        </>
                      )}
                    </section>
                  )}
                </IfPermissions>

                <section className="sidebar-section">
                  <div className="sidebar-section__heading">
                    <span>{t('manage')}</span>
                  </div>
                  <ul className="sidebar-section__list">
                    {manageLinksWithModules
                      .filter((item) => item.id !== 'document_config' || canViewDocumentConfig)
                      .map((item) => renderManageItem(item))}
                  </ul>
                </section>

                <section className="sidebar-section">
                  <div className="sidebar-section__heading">
                    <span>{t('administration')}</span>
                  </div>
                  <ul className="sidebar-section__list">{adminLinks.map((item) => renderNavItem(item))}</ul>
                </section>
              </div>
            </nav>

            {!slimSidebar && (
              <div className="sidebar__footer">
                <img
                  src={sidebarLogoSrc}
                  alt="Logo"
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  style={{ height: '2em', width: 'auto', objectFit: 'contain', maxWidth: '100%' }}
                />
              </div>
            )}

            {slimSidebar && !isMobile && slimPanelType && slimPanelTarget && (
              <Overlay
                show={!!slimPanelType && !!slimPanelTarget}
                target={slimPanelTarget}
                placement="right-start"
                rootClose
                onHide={closeSlimPanel}
                popperConfig={{
                  modifiers: [
                    { name: 'offset', options: { offset: [8, 0] } },
                    { name: 'flip', enabled: false },
                    { name: 'preventOverflow', enabled: true },
                  ],
                }}
              >
                {(overlayProps) => (
                  <div className="sidebar-slim-panel" {...overlayProps} onClick={(e) => e.stopPropagation()}>
                    <div className="sidebar-slim-panel__header">
                      <div className="sidebar-slim-panel__title-group">
                        <span className="sidebar-slim-panel__title">{slimPanelType === 'savedViews' ? t('saved_views') : t('open_documents')}</span>
                        <span className="sidebar-slim-panel__count">{slimPanelType === 'savedViews' ? savedViews.length : openDocuments.length}</span>
                      </div>
                      <button type="button" className="sidebar-slim-panel__close" onClick={closeSlimPanel} aria-label={t('close')}>
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                    <div className="sidebar-slim-panel__content">
                      {slimPanelType === 'savedViews' && (
                        <ul className="sidebar-slim-panel__list">
                          {savedViews.map((view) => (
                            <li key={view.id} className="sidebar-slim-panel__item">
                              <Link
                                className={`sidebar-link sidebar-link--panel ${isActive(`/view/${view.id}`) ? 'active' : ''}`}
                                to={`/view/${view.id}`}
                                onClick={() => {
                                  closeMenu()
                                  closeSlimPanel()
                                }}
                              >
                                <span className="sidebar-link__icon" aria-hidden="true">
                                  <i className="bi bi-funnel"></i>
                                </span>
                                <span className="sidebar-link__label">{view.name}</span>
                                {sidebarViewsShowCount && view.document_count !== undefined && (
                                  <span className="sidebar-link__badge sidebar-link__badge--sub">{view.document_count}</span>
                                )}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                      {slimPanelType === 'openDocuments' && (
                        <ul className="sidebar-slim-panel__list">
                          {openDocuments.map((doc) => (
                            <li key={doc.id} className="sidebar-slim-panel__item">
                              <Link
                                className={`sidebar-link sidebar-link--document sidebar-link--panel ${isActive(`/documents/${doc.id}`) ? 'active' : ''}`}
                                to={`/documents/${doc.id}/details`}
                                onClick={() => {
                                  closeMenu()
                                  closeSlimPanel()
                                }}
                              >
                                <span className="sidebar-link__icon" aria-hidden="true">
                                  <i className="bi bi-file-text"></i>
                                </span>
                                <span className="sidebar-link__label">{doc.title}</span>
                                <button
                                  type="button"
                                  className="sidebar-link__close"
                                  onClick={(event) => {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    closeDocument(doc, event)
                                    closeSlimPanel()
                                  }}
                                  aria-label={t('close')}
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              </Link>
                            </li>
                          ))}
                          {openDocuments.length >= 1 && (
                            <li className="sidebar-slim-panel__item">
                              <button
                                type="button"
                                className="sidebar-link sidebar-link--ghost sidebar-link--panel"
                                onClick={(event) => {
                                  event.preventDefault()
                                  closeAllDocuments(event)
                                  closeSlimPanel()
                                }}
                              >
                                <span className="sidebar-link__icon" aria-hidden="true">
                                  <i className="bi bi-x"></i>
                                </span>
                                <span>{t('close_all')}</span>
                              </button>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </Overlay>
            )}
          </div>
        </aside>
      </Collapse>

      {showCloseDocumentDialog && documentToClose && (
        <ConfirmDialog
          show={showCloseDocumentDialog}
          title={t('unsaved_changes')}
          messageBold={`${t('you_have_unsaved_changes_to_the_document')} "${documentToClose.title}".`}
          message={t('are_you_sure_you_want_to_close_this_document')}
          confirmText={t('close_document')}
          onConfirm={handleConfirmCloseDocument}
          onCancel={handleCancelCloseDocument}
          variant="warning"
        />
      )}

      {showCloseAllDocumentsDialog && (
        <ConfirmDialog
          show={showCloseAllDocumentsDialog}
          title={t('unsaved_changes')}
          messageBold={t('you_have_unsaved_changes')}
          message={t('are_you_sure_you_want_to_close_all_documents')}
          confirmText={t('close_documents')}
          onConfirm={handleConfirmCloseAllDocuments}
          onCancel={handleCancelCloseAllDocuments}
          variant="warning"
        />
      )}
    </>
  )
}
